/**
 * CSS custom-property emitter.
 *
 * The admin portal and the React Native Web build consume the same theme
 * objects as the phone app. Rather than maintaining a parallel stylesheet,
 * this flattens the theme into `--mfa-*` custom properties at build time, so
 * plain CSS can reference tokens and stay in sync automatically.
 *
 *   import { cssVariables, cssVar } from '@mfa/theme';
 *   document.head.append(styleTagWith(cssVariables()));
 *   // in CSS:  color: var(--mfa-text-primary);
 */

import { colors } from './semantic';
import { elevation, motion, radius, sizing, space, zIndex } from './layout';
import { fontSize, fontWeight, letterSpacing, textStyles } from './typography';

type Flat = Record<string, string | number>;

const kebab = (s: string): string =>
  s
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[_\s]+/g, '-')
    .toLowerCase();

/** Recursively flatten a token object into `prefix-path` keys. */
function flattenTokens(node: unknown, prefix: string, out: Flat): void {
  if (node === null || node === undefined) return;
  if (typeof node === 'string' || typeof node === 'number') {
    out[prefix] = node;
    return;
  }
  if (Array.isArray(node)) {
    node.forEach((v, i) => flattenTokens(v, `${prefix}-${i}`, out));
    return;
  }
  if (typeof node === 'object') {
    for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
      flattenTokens(value, prefix ? `${prefix}-${kebab(key)}` : kebab(key), out);
    }
  }
}

/** The full token set as flat `--mfa-*` names. */
export function tokenMap(): Flat {
  const out: Flat = {};
  flattenTokens(colors, 'mfa', out);
  flattenTokens({ space }, 'mfa', out);
  flattenTokens({ radius }, 'mfa', out);
  flattenTokens({ size: sizing }, 'mfa', out);
  flattenTokens({ z: zIndex }, 'mfa', out);
  flattenTokens({ fontSize, fontWeight, letterSpacing }, 'mfa', out);
  flattenTokens({ duration: motion.duration, easing: motion.easing }, 'mfa', out);

  // Shadows: emit only the CSS form, not the React Native fields.
  for (const [name, value] of Object.entries(elevation)) {
    out[`mfa-shadow-${kebab(name)}`] = value.css;
  }
  return out;
}

const UNITLESS = new Set(['mfa-font-weight', 'mfa-z', 'mfa-duration']);

const isUnitless = (key: string): boolean =>
  [...UNITLESS].some((p) => key.startsWith(p)) ||
  key.startsWith('mfa-size-drawer-width-ratio');

/** Serialise the token set into a `:root { ... }` block. */
export function cssVariables(selector = ':root'): string {
  const entries = Object.entries(tokenMap())
    .map(([key, value]) => {
      const printed =
        typeof value === 'number' && !isUnitless(key) ? `${value}px` : String(value);
      return `  --${key}: ${printed};`;
    })
    .sort();
  return `${selector} {\n${entries.join('\n')}\n}\n`;
}

/** Reference a token from JS: `cssVar('text-primary')` → `var(--mfa-text-primary)`. */
export const cssVar = (name: string, fallback?: string): string =>
  fallback ? `var(--mfa-${name}, ${fallback})` : `var(--mfa-${name})`;

/** Text roles as ready-to-use CSS declaration blocks, e.g. for a utility sheet. */
export function textRoleClasses(prefix = 'mfa-text'): string {
  return Object.entries(textStyles)
    .map(([role, s]) => {
      const decls = [
        `font-size: ${s.fontSize}px`,
        `font-weight: ${s.fontWeight}`,
        `line-height: ${s.lineHeight}px`,
        s.letterSpacing !== undefined ? `letter-spacing: ${s.letterSpacing}px` : null,
        s.textTransform ? `text-transform: ${s.textTransform}` : null,
      ].filter(Boolean);
      return `.${prefix}-${kebab(role)} { ${decls.join('; ')}; }`;
    })
    .join('\n');
}
